import { useState, useEffect } from 'react';
import API from './api';

export const PAGE_SCHEMAS = {
  homepage: {
    label: 'Homepage',
    description: 'Main landing page — hero, about, approach, CTA sections',
    sections: [
      { title: 'Hero Section', fields: [
        { key: 'hero_label', label: 'Top Label', type: 'text', default: 'Premium Luxury Rentals' },
        { key: 'hero_title', label: 'Main Heading', type: 'text', default: 'A World of Luxury, Comfort & Elegance' },
        { key: 'hero_description', label: 'Description', type: 'textarea', default: 'Discover premium short-term rentals across Ontario\'s most desirable cities. Your perfect stay awaits.' },
        { key: 'hero_btn_primary', label: 'Primary Button', type: 'text', default: 'Explore Residences' },
        { key: 'hero_btn_secondary', label: 'Secondary Button', type: 'text', default: 'Get In Touch' },
      ]},
      { title: 'Who We Are', fields: [
        { key: 'who_label', label: 'Section Label', type: 'text', default: 'Who We Are' },
        { key: 'who_title', label: 'Heading', type: 'text', default: 'Uncover Hidden Gems, Immerse in the Stories' },
        { key: 'who_description', label: 'Description', type: 'textarea', default: 'In a market saturated with options, we choose clarity. Where others see accommodations, we see transformations. Our commitment extends beyond check-in — we\'re architects of unforgettable experiences.' },
        { key: 'who_image', label: 'Image', type: 'image', default: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&auto=format' },
        { key: 'who_stat1_val', label: 'Stat 1 Value', type: 'text', default: '4' },
        { key: 'who_stat1_label', label: 'Stat 1 Label', type: 'text', default: 'Cities' },
        { key: 'who_stat2_val', label: 'Stat 2 Value', type: 'text', default: '100%' },
        { key: 'who_stat2_label', label: 'Stat 2 Label', type: 'text', default: 'Satisfaction' },
        { key: 'who_stat3_val', label: 'Stat 3 Value', type: 'text', default: '50+' },
        { key: 'who_stat3_label', label: 'Stat 3 Label', type: 'text', default: 'Properties' },
      ]},
      { title: 'Featured Properties', fields: [
        { key: 'featured_label', label: 'Section Label', type: 'text', default: 'Our Collection' },
        { key: 'featured_title', label: 'Heading', type: 'text', default: 'Discover the Finest Stays' },
      ]},
      { title: 'Our Approach', fields: [
        { key: 'approach_label', label: 'Section Label', type: 'text', default: 'How It Works' },
        { key: 'approach_title', label: 'Heading', type: 'text', default: 'Your Journey Begins Here' },
        { key: 'approach_step1_title', label: 'Step 1 Title', type: 'text', default: 'Browse & Discover' },
        { key: 'approach_step1_text', label: 'Step 1 Text', type: 'text', default: 'Explore our curated collection of luxury properties across Ontario' },
        { key: 'approach_step2_title', label: 'Step 2 Title', type: 'text', default: 'Choose Your Stay' },
        { key: 'approach_step2_text', label: 'Step 2 Text', type: 'text', default: 'Select the perfect property that matches your style and preferences' },
        { key: 'approach_step3_title', label: 'Step 3 Title', type: 'text', default: 'Book Directly' },
        { key: 'approach_step3_text', label: 'Step 3 Text', type: 'text', default: 'Seamless booking with instant confirmation and secure payment' },
        { key: 'approach_step4_title', label: 'Step 4 Title', type: 'text', default: 'Enjoy Your Stay' },
        { key: 'approach_step4_text', label: 'Step 4 Text', type: 'text', default: 'Arrive to a perfectly prepared residence with premium amenities' },
      ]},
      { title: 'Locations Showcase', fields: [
        { key: 'locations_label', label: 'Section Label', type: 'text', default: 'Our Destinations' },
        { key: 'locations_title', label: 'Heading', type: 'text', default: 'Explore Our Locations' },
      ]},
      { title: 'Testimonials Section', fields: [
        { key: 'testimonials_label', label: 'Section Label', type: 'text', default: 'Testimonials' },
        { key: 'testimonials_title', label: 'Heading', type: 'text', default: 'What Our Guests Say' },
        { key: 'testimonials_description', label: 'Description', type: 'text', default: 'From immersive experiences to unforgettable stays, our guests share their stories.' },
      ]},
      { title: 'Call to Action', fields: [
        { key: 'cta_label', label: 'Section Label', type: 'text', default: 'Start Your Journey' },
        { key: 'cta_title', label: 'Heading', type: 'text', default: 'Ready to Experience Luxury Living?' },
        { key: 'cta_description', label: 'Description', type: 'textarea', default: 'Browse our curated collection of premium properties and find your ideal Ontario residence.' },
        { key: 'cta_btn_primary', label: 'Primary Button', type: 'text', default: 'Browse Properties' },
        { key: 'cta_btn_secondary', label: 'Secondary Button', type: 'text', default: 'Contact Us' },
        { key: 'cta_image', label: 'Background Image', type: 'image', default: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&auto=format' },
      ]},
      { title: 'Marquee / Ticker', fields: [
        { key: 'marquee_text1', label: 'Marquee Text 1', type: 'text', default: 'Luxury Living' },
        { key: 'marquee_text2', label: 'Marquee Text 2', type: 'text', default: 'Premium Stays' },
      ]},
    ],
  },
  about: {
    label: 'About Page',
    description: 'Company story, mission, and values',
    sections: [
      { title: 'Hero Section', fields: [
        { key: 'hero_label', label: 'Top Label', type: 'text', default: 'Our Story' },
        { key: 'hero_title', label: 'Heading', type: 'text', default: 'Redefining Luxury Stays Across Ontario' },
        { key: 'hero_description', label: 'Description', type: 'textarea', default: 'We believe that where you stay should be more than just a place to sleep — it should be an experience that inspires, rejuvenates, and creates lasting memories.' },
      ]},
      { title: 'Who We Are', fields: [
        { key: 'who_label', label: 'Section Label', type: 'text', default: 'Who We Are' },
        { key: 'who_title', label: 'Heading', type: 'text', default: 'A New Standard in Luxury Stays' },
        { key: 'who_description1', label: 'Paragraph 1', type: 'textarea', default: 'Plush Residence is a premium short-term rental company operating across four of Ontario\'s most vibrant cities: Toronto, Mississauga, Niagara Falls, and Ottawa. We curate and manage a portfolio of exceptional properties that offer the comforts of home with the luxury of a five-star hotel.' },
        { key: 'who_description2', label: 'Paragraph 2', type: 'textarea', default: 'Every property in our collection is handpicked, professionally styled, and maintained to the highest standards. From the moment you arrive, you\'ll experience the difference that thoughtful curation and genuine hospitality make.' },
        { key: 'who_image', label: 'Image', type: 'image', default: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&auto=format' },
      ]},
      { title: 'Stats', fields: [
        { key: 'stat1_value', label: 'Stat 1 Value', type: 'text', default: '4' },
        { key: 'stat1_label', label: 'Stat 1 Label', type: 'text', default: 'Cities' },
        { key: 'stat2_value', label: 'Stat 2 Value', type: 'text', default: '50+' },
        { key: 'stat2_label', label: 'Stat 2 Label', type: 'text', default: 'Properties' },
        { key: 'stat3_value', label: 'Stat 3 Value', type: 'text', default: '100%' },
        { key: 'stat3_label', label: 'Stat 3 Label', type: 'text', default: 'Satisfaction' },
        { key: 'stat4_value', label: 'Stat 4 Value', type: 'text', default: '1000+' },
        { key: 'stat4_label', label: 'Stat 4 Label', type: 'text', default: 'Happy Guests' },
      ]},
      { title: 'Our Values', fields: [
        { key: 'values_label', label: 'Section Label', type: 'text', default: 'Our Values' },
        { key: 'values_title', label: 'Heading', type: 'text', default: 'What Sets Us Apart' },
        { key: 'value1_title', label: 'Value 1 Title', type: 'text', default: 'Quality Assured' },
        { key: 'value1_text', label: 'Value 1 Text', type: 'text', default: 'Every property meets our rigorous quality checklist before being listed' },
        { key: 'value2_title', label: 'Value 2 Title', type: 'text', default: 'Personal Touch' },
        { key: 'value2_text', label: 'Value 2 Text', type: 'text', default: 'Thoughtful amenities, local recommendations, and genuine hospitality' },
        { key: 'value3_title', label: 'Value 3 Title', type: 'text', default: 'Premium Comfort' },
        { key: 'value3_text', label: 'Value 3 Text', type: 'text', default: 'Hotel-quality linens, fully equipped kitchens, and designer furnishings' },
        { key: 'value4_title', label: 'Value 4 Title', type: 'text', default: 'Expert Management' },
        { key: 'value4_text', label: 'Value 4 Text', type: 'text', default: 'Professional cleaning, 24/7 guest support, and seamless check-in' },
      ]},
      { title: 'CTA Section', fields: [
        { key: 'cta_title', label: 'CTA Heading', type: 'text', default: 'Ready to Experience Plush?' },
        { key: 'cta_description', label: 'CTA Description', type: 'text', default: 'Browse our locations and find the perfect property for your next Ontario getaway.' },
        { key: 'cta_btn', label: 'CTA Button Text', type: 'text', default: 'Explore Locations' },
      ]},
      { title: 'Content', fields: [
        { key: 'body', label: 'Page Content (Rich Text)', type: 'richtext', default: '' },
      ]},
    ],
  },
  contact: {
    label: 'Contact Page',
    description: 'Contact form page heading and description',
    sections: [
      { title: 'Hero Section', fields: [
        { key: 'hero_label', label: 'Top Label', type: 'text', default: 'Get In Touch' },
        { key: 'hero_title', label: 'Heading', type: 'text', default: 'We\'d Love to Hear From You' },
        { key: 'hero_description', label: 'Description', type: 'textarea', default: 'Whether you have questions about our properties or need help planning your stay, our team is here to assist you.' },
      ]},
      { title: 'Form Labels', fields: [
        { key: 'form_name_label', label: 'Name Field Label', type: 'text', default: 'Name' },
        { key: 'form_email_label', label: 'Email Field Label', type: 'text', default: 'Email' },
        { key: 'form_phone_label', label: 'Phone Field Label', type: 'text', default: 'Phone (Optional)' },
        { key: 'form_message_label', label: 'Message Field Label', type: 'text', default: 'Message' },
        { key: 'form_btn_text', label: 'Submit Button Text', type: 'text', default: 'Send Message' },
        { key: 'form_success_msg', label: 'Success Message', type: 'text', default: 'Message sent! We\'ll get back to you within 24 hours.' },
      ]},
      { title: 'Contact Info', fields: [
        { key: 'info_heading', label: 'Info Section Heading', type: 'text', default: 'Contact Information' },
        { key: 'email_label', label: 'Email Label', type: 'text', default: 'Email' },
        { key: 'phone_label', label: 'Phone Label', type: 'text', default: 'Phone' },
        { key: 'office_label', label: 'Office Label', type: 'text', default: 'Office' },
      ]},
    ],
  },
  policies: {
    label: 'Policies Page',
    description: 'Privacy policy, terms, cancellation and house rules',
    sections: [
      { title: 'Hero Section', fields: [
        { key: 'label', label: 'Top Label', type: 'text', default: 'Information' },
        { key: 'title', label: 'Page Title', type: 'text', default: 'Policies & FAQ' },
      ]},
      { title: 'Policy Sections', fields: [
        { key: 'section1_title', label: 'Section 1 Title', type: 'text', default: 'Booking & Cancellation Policy' },
        { key: 'section1_items', label: 'Section 1 Items (one per line)', type: 'textarea', default: 'All bookings are confirmed upon completion of payment through our Hostaway booking platform.\nCancellation policies vary by property. Please review the specific cancellation terms on the booking page before completing your reservation.\nModifications to reservations are subject to availability and may incur additional charges.\nEarly check-in and late check-out requests are accommodated when possible, subject to availability.' },
        { key: 'section2_title', label: 'Section 2 Title', type: 'text', default: 'Check-In & Check-Out' },
        { key: 'section2_items', label: 'Section 2 Items (one per line)', type: 'textarea', default: 'Standard check-in time is 3:00 PM and check-out time is 11:00 AM.\nSelf check-in instructions will be provided 24 hours before your arrival.\nKeys or access codes will be shared via email and our guest portal.\nPlease ensure the property is left in a reasonable condition upon check-out.' },
        { key: 'section3_title', label: 'Section 3 Title', type: 'text', default: 'House Rules' },
        { key: 'section3_items', label: 'Section 3 Items (one per line)', type: 'textarea', default: 'No smoking inside any of our properties. Designated outdoor areas may be available.\nQuiet hours are from 10:00 PM to 8:00 AM.\nPets are allowed in select properties only. Please confirm pet policy before booking.\nNo parties or events are permitted without prior written approval.\nMaximum occupancy must be respected as indicated in the listing details.' },
        { key: 'section4_title', label: 'Section 4 Title', type: 'text', default: 'Privacy Policy' },
        { key: 'section4_items', label: 'Section 4 Items (one per line)', type: 'textarea', default: 'We collect personal information solely for the purpose of managing your booking and improving our services.\nYour data is never sold to third parties.\nPayment information is processed securely through Stripe via our Hostaway integration.\nYou may request deletion of your data at any time by contacting us.' },
        { key: 'section5_title', label: 'Section 5 Title', type: 'text', default: 'Liability & Damages' },
        { key: 'section5_items', label: 'Section 5 Items (one per line)', type: 'textarea', default: 'Guests are responsible for any damage to the property or its contents during their stay.\nA security deposit may be held and returned after a satisfactory check-out inspection.\nPlush Residence is not liable for personal belongings left at any property.' },
      ]},
      { title: 'Rich Text Override', fields: [
        { key: 'body', label: 'Content (Rich Text)', type: 'richtext', default: '' },
      ]},
    ],
  },
  locations: {
    label: 'Locations Page',
    description: 'Locations listing page',
    sections: [
      { title: 'Hero Section', fields: [
        { key: 'hero_label', label: 'Top Label', type: 'text', default: 'Discover' },
        { key: 'hero_title', label: 'Heading', type: 'text', default: 'Our Locations' },
        { key: 'hero_description', label: 'Description', type: 'text', default: 'Premium short-term rentals across Ontario\'s most desirable cities' },
      ]},
    ],
  },
  footer: {
    label: 'Footer',
    description: 'Footer text, headings, copyright',
    sections: [
      { title: 'Footer Content', fields: [
        { key: 'tagline', label: 'Tagline', type: 'text', default: 'Experience luxury living at its finest' },
        { key: 'description', label: 'Description', type: 'textarea', default: 'Premium short-term luxury rentals across Ontario\'s most desirable cities. Designed for those who appreciate the finer things.' },
        { key: 'copyright', label: 'Copyright Text', type: 'text', default: '\u00a9 2026 Plush Residence. All rights reserved.' },
        { key: 'quick_links_heading', label: 'Quick Links Heading', type: 'text', default: 'Quick Links' },
        { key: 'locations_heading', label: 'Locations Heading', type: 'text', default: 'Our Locations' },
        { key: 'contact_heading', label: 'Contact Heading', type: 'text', default: 'Get In Touch' },
        { key: 'privacy_text', label: 'Privacy Link Text', type: 'text', default: 'Privacy Policy' },
        { key: 'terms_text', label: 'Terms Link Text', type: 'text', default: 'Terms of Service' },
        { key: 'cookie_text', label: 'Cookie Link Text', type: 'text', default: 'Cookie Policy' },
      ]},
    ],
  },
  header: {
    label: 'Header / Navigation',
    description: 'Site logo, navigation links',
    sections: [
      { title: 'Branding', fields: [
        { key: 'site_name', label: 'Site Name', type: 'text', default: 'PLUSH' },
      ]},
    ],
  },
};

function getDefaults(pageKey) {
  const schema = PAGE_SCHEMAS[pageKey];
  if (!schema) return {};
  const defaults = {};
  schema.sections.forEach(s => s.fields.forEach(f => { defaults[f.key] = f.default; }));
  return defaults;
}

export function useContent(pageKey) {
  const [content, setContent] = useState(() => getDefaults(pageKey));
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    API.get(`/pages/${pageKey}`).then(r => {
      const saved = r.data || {};
      setContent(prev => ({ ...prev, ...saved }));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [pageKey]);

  const get = (key) => {
    if (content[key] !== undefined && content[key] !== '') return content[key];
    const defaults = getDefaults(pageKey);
    return defaults[key] || '';
  };

  return { content, get, loaded };
}
